// The one-time secret dialog of the billing admin's AWS accounts (installation secret) and of the
// Keys screen's API keys, in both shells: the secret is shown once, and the dialog cannot be
// dismissed until the operator ticks that they copied it (`canDismissSecretDialog`).
import { useState } from "preact/hooks";
import { Dialog } from "../../primitives/dialog";
import { Checkbox } from "../../primitives/field";
import { Icon } from "../../primitives/icons";
import { acknowledgeSecretDialog, canDismissSecretDialog, type SecretDialogState } from "./credentials-model";

export interface SecretDialogProps {
  title: string;
  /** Prefix for the checkbox id (two dialogs never share one). */
  id: string;
  secret: SecretDialogState | null;
  onChange(next: SecretDialogState | null): void;
}

export function SecretDialog({ title, id, secret, onChange }: SecretDialogProps) {
  const [copied, setCopied] = useState(false);
  const close = () => {
    setCopied(false);
    onChange(null);
  };
  return (
    <Dialog
      title={title}
      open={secret !== null}
      onClose={() => canDismissSecretDialog(secret) && close()}
      actions={
        <button type="button" class="button button-primary" disabled={!secret?.acknowledged} onClick={close}>
          Close
        </button>
      }
    >
      {secret && (
        <div class="stack-sm">
          <p>Copy this secret now — you will not see it again.</p>
          <code class="secret-value">{secret.secret}</code>
          <div class="row">
            <button
              type="button"
              class="button button-sm"
              onClick={() => void navigator.clipboard?.writeText(secret.secret).then(() => setCopied(true), () => setCopied(false))}
            >
              <Icon name={copied ? "check" : "copy"} small />
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <Checkbox
            id={`${id}-copied`}
            label="I have copied this secret"
            help="customers.secretCopied"
            checked={secret.acknowledged}
            onChange={() => onChange(acknowledgeSecretDialog(secret))}
          />
        </div>
      )}
    </Dialog>
  );
}
